import React, { useEffect, useState, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import Select from "react-select";
import SweetAlert from "react-bootstrap-sweetalert";
import {
  createSectionItemApi,
  getSectionItemByIdApi,
  updateSectionItemApi,
  searchMediaApi,
} from "../../Constants/api";
import { getAllSectionsApi, getSectionByIdApi } from "../../../Sections/Constants/api";
import debounce from "lodash/debounce";

const CreateSectionItem = () => {
  const history = useHistory();
  const { id } = useParams();
  const [formData, setFormData] = useState({
    videoId: "",
    sectionId: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(null);
  const [errors, setErrors] = useState({});

  // Dropdown States for Videos
  const [videoOptions, setVideoOptions] = useState([]);
  const [videoPage, setVideoPage] = useState(1);
  const [videoHasMore, setVideoHasMore] = useState(true);
  const [isLoadingVideos, setIsLoadingVideos] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [videoSearchKey, setVideoSearchKey] = useState("");

  // Dropdown States for Sections
  const [sectionOptions, setSectionOptions] = useState([]);
  const [sectionPage, setSectionPage] = useState(1);
  const [sectionHasMore, setSectionHasMore] = useState(true);
  const [isLoadingSections, setIsLoadingSections] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);

  const { videoId, sectionId } = formData;

  useEffect(() => {
    fetchVideos("", 1);
    fetchSections(1);
    if (id) {
      fetchSectionItemDetails(id);
    }
  }, [id]);

  const fetchSectionItemDetails = async (itemId) => {
    try {
      const { data } = await getSectionItemByIdApi(itemId);
      if (data && data.data) {
        const item = data.data;
        const vId = typeof item.videoId === 'object' ? item.videoId?._id : item.videoId;
        const sId = typeof item.sectionId === 'object' ? item.sectionId?._id : item.sectionId;

        setFormData({
          videoId: vId || "",
          sectionId: sId || "",
        });

        // If labels are missing, fetch them
        if (vId) {
          if (typeof item.videoId === 'object' && item.videoId.title) {
            setSelectedVideo({ label: item.videoId.title, value: vId });
          }
        }

        if (sId) {
          if (typeof item.sectionId === 'object' && item.sectionId.title) {
            setSelectedSection({ label: item.sectionId.title, value: sId });
          } else {
            const { data: sData } = await getSectionByIdApi(sId);
            if (sData?.data) {
              setSelectedSection({ label: sData.data.title, value: sId });
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching section item details:", error);
    }
  };

  const fetchVideos = async (searchKey, pageNum) => {
    setIsLoadingVideos(true);
    try {
      const { data } = await searchMediaApi(searchKey, pageNum, 10);
      const newItems = data?.data || [];
      const options = newItems.map(item => ({
        label: item.title || "Untitled Video",
        value: item._id
      }));

      if (pageNum === 1) {
        setVideoOptions(options);
        setVideoPage(1);
        setVideoHasMore(newItems.length === 10);
      } else {
        setVideoOptions(prev => [...prev, ...options]);
        setVideoHasMore(newItems.length === 10);
      }
    } catch (error) {
      console.error("Error fetching videos:", error);
    } finally {
      setIsLoadingVideos(false);
    }
  };

  const debouncedVideoSearch = useCallback(
    debounce((searchKey) => {
      fetchVideos(searchKey, 1);
    }, 500),
    []
  );

  const handleVideoInputChange = (inputValue) => {
    setVideoSearchKey(inputValue);
    debouncedVideoSearch(inputValue);
  };

  const fetchSections = async (pageNum) => {
    setIsLoadingSections(true);
    try {
      const { data } = await getAllSectionsApi(pageNum, 10);
      const newItems = data?.data || [];
      const options = newItems.map(item => ({
        label: item.title || "Untitled Section",
        value: item._id
      }));

      setSectionOptions(prev => pageNum === 1 ? options : [...prev, ...options]);
      if (newItems.length < 10) setSectionHasMore(false);
    } catch (error) {
      console.error("Error fetching sections:", error);
    } finally {
      setIsLoadingSections(false);
    }
  };

  const handleVideoScroll = () => {
    if (videoHasMore && !isLoadingVideos) {
      const nextPage = videoPage + 1;
      setVideoPage(nextPage);
      fetchVideos(videoSearchKey, nextPage);
    }
  };

  const handleSectionScroll = () => {
    if (sectionHasMore && !isLoadingSections) {
      const nextPage = sectionPage + 1;
      setSectionPage(nextPage);
      fetchSections(nextPage);
    }
  };

  const handleVideoChange = (selectedOption) => {
    setSelectedVideo(selectedOption);
    setFormData({ ...formData, videoId: selectedOption ? selectedOption.value : "" });
    if (errors.videoId) setErrors({ ...errors, videoId: null });
  };

  const handleSectionChange = (selectedOption) => {
    setSelectedSection(selectedOption);
    setFormData({ ...formData, sectionId: selectedOption ? selectedOption.value : "" });
    if (errors.sectionId) setErrors({ ...errors, sectionId: null });
  };

  const validate = () => {
    const newErrors = {};
    if (!videoId) newErrors.videoId = "Please select a video";
    if (!sectionId) newErrors.sectionId = "Please select a section";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setIsLoading(true);
    try {
      const payload = { videoId, sectionId };
      if (id) {
        await updateSectionItemApi(id, payload);
      } else {
        await createSectionItemApi(payload);
      }

      setAlert(
        <SweetAlert
          success
          title={id ? "Updated!" : "Created!"}
          onConfirm={() => {
            setAlert(null);
            history.push("/dashboard/section-items/all");
          }}
        >
          Section Item has been {id ? "updated" : "created"} successfully.
        </SweetAlert>
      );
    } catch (error) {
      console.error("Error submitting form:", error);
      setAlert(<SweetAlert danger title="Error" onConfirm={() => setAlert(null)}>Something went wrong.</SweetAlert>);
    } finally {
      setIsLoading(false);
    }
  };

  const ErrorMsg = ({ msg }) => (
    msg ? <div style={{ color: "red", fontSize: "12px", marginTop: "5px" }}>{msg}</div> : null
  );

  return (
    <div className="card">
      <div className="card-header">
        <h3>{id ? "Edit Section Item" : "Create Section Item"}</h3>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Select Video <span className="text-danger">*</span></label>
            <Select
              options={videoOptions}
              value={selectedVideo}
              onChange={handleVideoChange}
              onInputChange={handleVideoInputChange}
              onMenuScrollToBottom={handleVideoScroll}
              isLoading={isLoadingVideos}
              placeholder="Type to search video..."
              disabled={isLoading}
              isClearable
            />
            <ErrorMsg msg={errors.videoId} />
          </div>

          <div className="form-group">
            <label>Select Section <span className="text-danger">*</span></label>
            <Select
              options={sectionOptions}
              value={selectedSection}
              onChange={handleSectionChange}
              onMenuScrollToBottom={handleSectionScroll}
              isLoading={isLoadingSections}
              placeholder="Select a section..."
              disabled={isLoading}
              isClearable
            />
            <ErrorMsg msg={errors.sectionId} />
          </div>

          <button type="submit" className="btn btn-primary mt-3" disabled={isLoading}>
            {isLoading ? "Saving..." : (id ? "Update" : "Create")}
          </button>
          {id && (
            <button type="button" className="btn btn-secondary mt-3 ml-2" onClick={() => history.push("/dashboard/section-items/all")} disabled={isLoading}>
              Cancel
            </button>
          )}
        </form>
      </div>
      {alert}
    </div>
  );
};

export default CreateSectionItem;
